import { useState, useEffect } from 'react';
import {
    TrendingUp,
    Download,
    Truck,
    AlertTriangle,
    DollarSign,
    Clock,
    Loader2,
    ShoppingBag,
    Users,
    Activity,
    ArrowUpRight,
    Shield,
    RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area
} from 'recharts';
import { reporteService } from '../../services/maintenance/reporteService';
import toast from 'react-hot-toast';

const COLORS = ['#3f51b5', '#00bcd4', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#795548'];

export default function ReportesMantenimiento() {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await reporteService.getDashboardStats();
            if (response.success) {
                setStats(response.data);
                if (silent) toast.success('Métricas actualizadas');
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
            toast.error('No se pudieron cargar las estadísticas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const handleDownloadExcel = async () => {
        try {
            toast.loading('Generando reporte Excel...');
            await reporteService.descargarReporteEjecutivo();
            toast.dismiss();
            toast.success('Reporte descargado');
        } catch (error) {
            toast.dismiss();
            toast.error('Error al descargar el reporte');
        }
    };

    if (loading) {
        return (
            <div className="flex h-[600px] items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="relative">
                        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                        <Activity className="h-5 w-5 text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-bold text-gray-800">Generando Métricas Globales</p>
                        <p className="text-xs text-gray-400 mt-1">Analizando flota, costos y consumos históricos...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats) return null;

    const last12Months = Array.from({ length: 12 }, (_unused, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() - (11 - i));
        const monthName = d.toLocaleString('default', { month: 'short' }).toUpperCase();
        const yearSuffix = d.getFullYear();
        return {
            mes: monthName,
            key: `${monthName}-${yearSuffix}`,
            originalDate: new Date(d.getFullYear(), d.getMonth(), 1)
        };
    });

    const costsByMonth = last12Months.map((m: any) => {
        const found = (stats.costos?.por_mes || []).find((d: any) => {
            const dDate = new Date(d.mes + '-01');
            return dDate.getMonth() === m.originalDate.getMonth() &&
                dDate.getFullYear() === m.originalDate.getFullYear();
        });
        return {
            mes: m.mes,
            monto: found ? parseFloat(found.total) : 0
        };
    });

    // Categorias de costos para la dona
    const chartCategoryData = (Array.isArray(stats.costos?.por_categoria) ? stats.costos.por_categoria : [])
        .map((cat: any) => ({
            name: cat.categoria || 'Sin Categoría',
            value: parseFloat(cat.total)
        }))
        .filter((cat: any) => cat.value > 0);

    return (
        <div className="p-6 space-y-8 bg-gray-50/30 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Reporte Mantenimiento General</h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">Control detallado de flota, costos y cumplimiento operativo</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={() => fetchStats(true)}
                        disabled={loading}
                        className="p-6 rounded-xl border-gray-200 hover:bg-white hover:border-blue-400 hover:text-blue-600 transition-all group shadow-sm bg-white"
                        title="Actualizar datos"
                    >
                        <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                    </Button>
                    <Button
                        onClick={handleDownloadExcel}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-6 rounded-xl shadow-lg shadow-blue-200 transition-all flex items-center gap-2"
                    >
                        <Download className="h-5 w-5" />
                        Exportar Reporte Ejecutivo
                    </Button>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="border-none shadow-sm bg-blue-50/70">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600 uppercase tracking-wider text-[10px]">Disponibilidad</p>
                                <h3 className="text-2xl font-black text-blue-900 mt-1">{stats.flota.disponibilidad}%</h3>
                                <p className="text-[10px] text-blue-600/70 mt-1 font-bold">{stats.flota.operativos} operativos de {stats.flota.total}</p>
                            </div>
                            <div className="p-3 bg-blue-100/50 rounded-xl">
                                <Truck className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-none shadow-sm bg-green-50/70">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600 uppercase tracking-wider text-[10px]">Inversión Total</p>
                                <h3 className="text-2xl font-black text-green-900 mt-1">S/ {stats.costos.total.toLocaleString()}</h3>
                                <div className="flex items-center gap-1 mt-1">
                                    <ArrowUpRight className="h-3 w-3 text-green-600" />
                                    <p className="text-[10px] text-green-600/70 font-bold">Acumulado {new Date().getFullYear()}</p>
                                </div>
                            </div>
                            <div className="p-3 bg-green-100/50 rounded-xl">
                                <DollarSign className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-none shadow-sm bg-amber-50/70">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-amber-600 uppercase tracking-wider text-[10px]">Cumplimiento</p>
                                <h3 className="text-2xl font-black text-amber-900 mt-1">{stats.cumplimiento_preventivos}%</h3>
                                <p className="text-[10px] text-amber-600/70 mt-1 font-bold">{stats.preventivos.realizados} OTs Ejecutadas</p>
                            </div>
                            <div className="p-3 bg-amber-100/50 rounded-xl">
                                <Shield className="h-6 w-6 text-amber-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-none shadow-sm bg-red-50/70">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-red-600 uppercase tracking-wider text-[10px]">Resolución Promedio</p>
                                <h3 className="text-2xl font-black text-red-900 mt-1">{stats.ordenes.tiempo_promedio_resolucion} Días</h3>
                                <p className="text-[10px] text-red-600/70 mt-1 font-bold">{stats.ordenes.abiertas} OTs Abiertas</p>
                            </div>
                            <div className="p-3 bg-red-100/50 rounded-xl">
                                <Clock className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts Section - Colorful Area and Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2 shadow-sm border-gray-100 overflow-hidden">
                    <CardHeader className="bg-white border-b border-gray-50 pb-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2 text-gray-800 uppercase tracking-tight">
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                            HISTÓRICO DE CONSUMO
                        </CardTitle>
                        <CardDescription className="text-xs">Resumen mensual de inversión en mantenimiento</CardDescription>
                    </CardHeader>
                    <CardContent className="h-[300px] pt-6 pr-6 pl-2">
                        <ResponsiveContainer width="100%" height="100%" debounce={150}>
                            <AreaChart data={costsByMonth} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorMonto" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="mes" fontSize={10} stroke="#94a3b8" tickLine={false} axisLine={false} dy={10} />
                                <YAxis fontSize={10} stroke="#94a3b8" tickLine={false} axisLine={false} tickFormatter={(val) => `S/ ${(val / 1000).toFixed(0)}k`} />
                                <RechartsTooltip
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    formatter={(value: any) => [`S/ ${value.toLocaleString()}`, 'Inversión']}
                                />
                                <Area type="monotone" dataKey="monto" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorMonto)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="shadow-sm border-gray-100 flex flex-col">
                    <CardHeader className="bg-white border-b border-gray-50 pb-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2 text-gray-800 uppercase tracking-tight">
                            <Activity className="h-5 w-5 text-purple-600" />
                            GASTO POR CATEGORÍA
                        </CardTitle>
                        <CardDescription className="text-xs">Distribución de recursos</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col pt-4">
                        <div className="h-[240px] w-full relative">
                            <ResponsiveContainer width="100%" height="100%" debounce={150}>
                                <PieChart>
                                    <Pie
                                        data={chartCategoryData.length > 0 ? chartCategoryData : [{ name: 'Sin Datos', value: 1 }]}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={85}
                                        paddingAngle={4}
                                        dataKey="value"
                                        isAnimationActive={true}
                                    >
                                        {chartCategoryData.length > 0 ? (
                                            chartCategoryData.map((_cat: any, index: number) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))
                                        ) : (
                                            <Cell fill="#e2e8f0" />
                                        )}
                                    </Pie>
                                    <RechartsTooltip formatter={(val: any) => `S/ ${val.toLocaleString()}`} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="space-y-2 mt-4">
                            {chartCategoryData.slice(0, 4).map((cat: any, _idx: number) => (
                                <div key={cat.name} className="flex items-center justify-between px-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[_idx] }}></div>
                                        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tighter">{cat.name}</span>
                                    </div>
                                    <span className="text-xs font-black text-gray-900">
                                        {((cat.value / (stats.costos.total || 1)) * 100).toFixed(1)}%
                                    </span>
                                </div>
                            ))}
                            {chartCategoryData.length === 0 && (
                                <p className="text-[10px] text-center text-gray-400 font-bold uppercase py-4">Sin consumos registrados</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Providers and Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="shadow-sm border-gray-100">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2 text-gray-800 uppercase tracking-tight">
                            <Users className="h-5 w-5 text-blue-600" />
                            RENDIMIENTO DE PROVEEDORES
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-gray-50 border-t border-gray-50">
                            {stats.proveedores.slice(0, 5).map((p: any) => (
                                <div key={p.id} className="px-6 py-4 flex flex-col gap-3 hover:bg-blue-50/20 transition-all border-b border-gray-50 last:border-0 group">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-3">
                                            <div className="flex flex-col">
                                                <p className="text-sm font-black text-gray-900 uppercase leading-none group-hover:text-blue-700 transition-colors">{p.razon_social}</p>
                                                <p className="text-[10px] text-gray-400 font-bold mt-1 tracking-tight">RUC: {p.ruc}</p>
                                            </div>
                                        </div>
                                        <Badge className={`rounded-full px-3 py-1 text-[9px] font-black ${p.calificacion === 'A' ? 'bg-blue-100 text-blue-700' :
                                            p.calificacion === 'B' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            {p.calificacion === 'A' ? 'Socio Estratégico' : p.calificacion === 'B' ? 'Confiable' : 'Regular'}
                                        </Badge>
                                    </div>

                                    <div className="grid grid-cols-3 gap-4 bg-gray-50/50 p-3 rounded-lg border border-gray-100/50">
                                        <div className="text-center border-r border-gray-200">
                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Inversión</p>
                                            <p className="text-xs font-black text-blue-700">S/ {parseFloat(p.total_comprado).toLocaleString()}</p>
                                        </div>
                                        <div className="text-center border-r border-gray-200">
                                            <p className="text-[9px] font-bold text-gray-400 uppercase">OTs Totales</p>
                                            <p className="text-xs font-black text-gray-900">{p.total_ots}</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-[9px] font-bold text-gray-400 uppercase">Eficiencia</p>
                                            <div className="flex items-center justify-center gap-1">
                                                <div className="flex flex-col items-center">
                                                    <span className="text-[8px] font-bold text-blue-500">P:{p.ots_preventivas}</span>
                                                    <span className="text-[8px] font-bold text-red-500">C:{p.ots_correctivas}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {stats.proveedores.length === 0 && (
                                <div className="p-12 text-center">
                                    <Users className="h-8 w-8 text-gray-100 mx-auto mb-2" />
                                    <p className="text-[10px] font-black text-gray-300 uppercase">Sin servicios de terceros registrados</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="shadow-sm border-gray-100">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg font-bold flex items-center gap-2 text-gray-800 uppercase tracking-tight">
                            <ShoppingBag className="h-5 w-5 text-green-600" />
                            REPUESTOS DE MAYOR USO
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-gray-50 border-t border-gray-50">
                            {stats.repuestos_mas_usados.slice(0, 6).map((item: any, idx: number) => {
                                const maxCost = parseFloat(stats.repuestos_mas_usados[0]?.costo_total || 1);
                                const intensity = (parseFloat(item.costo_total) / maxCost) * 100;

                                return (
                                    <div key={item.codigo_interno || idx} className="px-6 py-4 relative group hover:bg-green-50/10 transition-colors overflow-hidden border-b border-gray-50 last:border-0">
                                        <div
                                            className="absolute left-0 top-0 bottom-0 bg-green-500/5 transition-all duration-1000"
                                            style={{ width: `${intensity}%` }}
                                        />

                                        <div className="relative flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="flex flex-col items-center justify-center w-8 h-8 rounded-lg bg-white border border-gray-100 text-[10px] font-black text-gray-400 group-hover:text-green-600 transition-colors">
                                                    #{idx + 1}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-gray-800 uppercase line-clamp-1 group-hover:text-green-700 transition-colors">{item.nombre}</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">COD: {item.codigo_interno || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex items-center gap-2 justify-end">
                                                    <Badge className="bg-gray-100 text-gray-600 border-none px-2 py-0 text-[9px] font-black h-4">
                                                        {Math.round(item.cantidad_total)} {item.unidad_medida}
                                                    </Badge>
                                                    <p className="text-sm font-black text-green-700">S/ {parseFloat(item.costo_total).toLocaleString()}</p>
                                                </div>
                                                <p className="text-[9px] text-gray-400 font-bold uppercase mt-0.5">PROM: S/ {parseFloat(item.precio_promedio).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {stats.repuestos_mas_usados.length === 0 && (
                                <div className="p-12 text-center">
                                    <ShoppingBag className="h-8 w-8 text-gray-100 mx-auto mb-2" />
                                    <p className="text-[10px] font-black text-gray-300 uppercase">Sin movimientos registrados</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Critical Alerts and Expirations */}
            <Card className="shadow-sm border-red-100 bg-white">
                <CardHeader className="flex flex-row items-center justify-between pb-2 border-b border-gray-50">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-red-100 rounded-lg">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                            <CardTitle className="text-lg font-black text-gray-900 uppercase tracking-tighter">
                                VENCIMIENTOS Y ALERTAS CRÍTICAS
                            </CardTitle>
                            <CardDescription className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                                Atención inmediata requerida para la operatividad
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Badge className="bg-red-50 text-red-700 border-red-200 px-3 py-1 font-black text-[10px] rounded-full">
                            {stats.alertas.vencidos} VENCIDOS
                        </Badge>
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 px-3 py-1 font-black text-[10px] rounded-full">
                            {stats.alertas.por_vencer} POR VENCER
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    {stats.alertas.detalle.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {stats.alertas.detalle.slice(0, 6).map((alerta: any) => (
                                <div key={alerta.id} className="group flex flex-col p-5 rounded-2xl border border-gray-100 bg-white hover:border-red-200 hover:shadow-md transition-all active:scale-[0.98]">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex flex-col">
                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-full w-fit mb-1 uppercase ${alerta.severidad === 'CRITICAL' ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                {alerta.severidad === 'CRITICAL' ? 'CRÍTICO' : 'AVISO'}
                                            </span>
                                            <div className="flex items-center gap-1.5 mt-1">
                                                <Truck className="h-3 w-3 text-gray-400" />
                                                <span className="text-xs font-black text-blue-700 uppercase">{alerta.placa}</span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] text-gray-400 font-bold uppercase">{new Date(alerta.created_at).toLocaleDateString()}</p>
                                            <p className="text-[9px] text-gray-300 font-medium uppercase mt-0.5">{new Date(alerta.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                    <h4 className="text-xs font-bold text-gray-900 leading-relaxed min-h-[40px] line-clamp-2 uppercase">
                                        {alerta.mensaje}
                                    </h4>
                                    <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between">
                                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">{alerta.tipo.replace('_', ' ')}</span>
                                        <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-red-50 group-hover:text-red-600 transition-colors">
                                            <Activity className="h-3 w-3" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="py-16 flex flex-col items-center justify-center text-center">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                                <Shield className="h-8 w-8 text-green-500" />
                            </div>
                            <h3 className="text-sm font-black text-gray-900 uppercase">Sin alertas críticas</h3>
                            <p className="text-xs text-gray-400 font-medium mt-1">Tu flota se encuentra al día según los registros</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
